/**
 * Hooks del publicador del CM (docs/PUBLICADOR.md). Solo rol admin.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiGet, apiPost, apiPut } from "@/lib/api";

export type PublisherMode = "off" | "dry_run" | "active";

export const PUBLISHER_MODES: Array<{ value: PublisherMode; label: string; description: string }> = [
  { value: "off", label: "Apagado", description: "Publica Mission Control. El CM no mira las tareas." },
  { value: "dry_run", label: "Dry-run", description: "El CM registra qué haría, sin publicar. MC sigue publicando." },
  { value: "active", label: "Activo", description: "Publica el CM. Mission Control tiene que estar apagado para el cliente." },
];

export interface PublisherClient {
  clientSlug: string;
  platforms: string[];
  clickupLists: string[];
  mode: PublisherMode;
  updatedBy: string | null;
  updatedAt: string | null;
  activeAllowed: boolean;
}

export interface PublisherOverview {
  config: {
    webhookConfigured: boolean;
    clickupConfigured: boolean;
    zernioConfigured: boolean;
    sweepMinutes: number;
    webhookUrl: string;
  };
  clients: PublisherClient[];
}

export interface PublisherRun {
  id: number;
  clientSlug: string;
  clickupTaskId: string;
  customTaskId: string | null;
  taskName: string | null;
  mode: PublisherMode;
  trigger: string;
  decision: string;
  reason: string | null;
  plan: Record<string, unknown>;
  createdAt: string;
}

const KEY = ["admin", "publisher"];

export function usePublisher() {
  return useQuery<PublisherOverview>({
    queryKey: KEY,
    queryFn: () => apiGet<PublisherOverview>("/admin/publisher"),
  });
}

export function usePublisherRuns(client: string | null) {
  return useQuery<PublisherRun[]>({
    queryKey: [...KEY, "runs", client],
    queryFn: async () =>
      (await apiGet<{ runs: PublisherRun[] }>(`/admin/publisher/runs?limit=100${client ? `&client=${encodeURIComponent(client)}` : ""}`)).runs,
    refetchInterval: 30_000,
  });
}

export function useSetPublisherMode() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { clientSlug: string; mode: PublisherMode; confirmSlug?: string }>({
    mutationFn: ({ clientSlug, mode, confirmSlug }) =>
      apiPut(`/admin/publisher/${encodeURIComponent(clientSlug)}`, { mode, confirmSlug }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: KEY });
      toast.success(`${v.clientSlug}: publicador en ${PUBLISHER_MODES.find((m) => m.value === v.mode)?.label}`);
    },
    onError: (err) => toast.error(err.message),
  });
}

export function useRunSweep() {
  const qc = useQueryClient();
  return useMutation<{ results: unknown[] }, Error, void>({
    mutationFn: () => apiPost<{ results: unknown[] }>("/admin/publisher/sweep", {}),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: KEY });
      toast.success(`Barrido: ${d.results.length} tareas revisadas`);
    },
    onError: (err) => toast.error(err.message),
  });
}

export function useSetPublisherList() {
  const qc = useQueryClient();
  return useMutation<{ listName: string | null }, Error, { clientSlug: string; list: string | null }>({
    mutationFn: ({ clientSlug, list }) =>
      apiPut<{ listName: string | null }>(`/admin/publisher/${encodeURIComponent(clientSlug)}/list`, { list }),
    onSuccess: (d, v) => {
      qc.invalidateQueries({ queryKey: KEY });
      toast.success(d.listName ? `${v.clientSlug}: lista "${d.listName}" vinculada` : `${v.clientSlug}: lista desvinculada`);
    },
    onError: (err) => toast.error(err.message),
  });
}
